/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  ActionOverflow,
  Button,
  Dialog,
  Grid,
  InlineNotification,
  Link,
  Tag,
  TextField,
  Typography2,
} from '@coursera/cds-core';
import { EditIcon, ExternalLinkIcon, UnlinkIcon } from '@coursera/cds-icons';

import UnlinkSlackConfirm from 'bundles/slack-account-link/components/UnlinkSlackConfirm';
import { SLACK_ACCOUNT_STATUS } from 'bundles/slack-account-link/constants';
import type { SLACK_ACCOUNT_STATUS_TYPE } from 'bundles/slack-account-link/constants';
import type { SlackAccount } from 'bundles/slack-account-link/types/SlackAccount';
import { link, unlink } from 'bundles/slack-account-link/utills/SlackAccountAPIUtils';
import { getSlackSignupLink, getSlackTeamLink } from 'bundles/slack-account-link/utills/slackLinks';

import _t from 'i18n!nls/slack-account-link';

type Props = {
  id: string;
  degreeId: string;
  degreeName: string;
  partnerName: string;
  email?: string | null;
  status: SLACK_ACCOUNT_STATUS_TYPE;
  slackTeamDomainName?: string;
};

type Mode = 'view' | 'edit';

const NetworkState = {
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
} as const;

type NetworkStateType = (typeof NetworkState)[keyof typeof NetworkState];

const styles = {
  form: css`
    margin-top: var(--cds-spacing-100);
  `,
  infoNotification: css`
    margin-top: var(--cds-spacing-100);
  `,
  liveRegion: css`
    /* Only add spacing when a notification is visible */
    &:not(:empty) {
      margin-top: var(--cds-spacing-150);
    }
  `,
};

const SlackRow = ({
  id,
  degreeId,
  degreeName,
  partnerName,
  email: initialEmail,
  status,
  slackTeamDomainName,
}: Props) => {
  const [currentStatus, setCurrentStatus] = useState<SLACK_ACCOUNT_STATUS_TYPE>(status);
  // savedEmail is the last server-confirmed value; emailInput is what the user is typing.
  // Cancel resets emailInput back to savedEmail.
  const [savedEmail, setSavedEmail] = useState<string>(initialEmail ?? '');
  const [emailInput, setEmailInput] = useState<string>(initialEmail ?? '');
  const [mode, setMode] = useState<Mode>('view');
  const [networkStatus, setNetworkStatus] = useState<NetworkStateType | null>(null);
  const [showUnlinkModal, setShowUnlinkModal] = useState<boolean>(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const prevModeRef = useRef<Mode>(mode);

  // Manage focus when switching between view and edit mode.
  // Entering edit: focus the email input so the user can start typing immediately.
  // Leaving edit: return focus to the row so keyboard users aren't stranded on a
  // now-hidden form element.
  useEffect(() => {
    if (prevModeRef.current !== mode) {
      if (mode === 'edit') {
        emailInputRef.current?.focus();
      } else {
        rowRef.current?.focus();
      }
      prevModeRef.current = mode;
    }
  }, [mode]);

  const isLinked = currentStatus === SLACK_ACCOUNT_STATUS.EMAIL_VERIFIED;
  const showFormView = mode === 'edit';

  const slackTeamLink = getSlackTeamLink(slackTeamDomainName);
  const slackSignupLink = getSlackSignupLink(slackTeamDomainName);

  const handleLink = () => {
    setNetworkStatus(NetworkState.IN_PROGRESS);

    link(degreeId, emailInput)
      .then((result: SlackAccount) => {
        const newEmail = result.email || '';
        setSavedEmail(newEmail);
        setEmailInput(newEmail);
        setCurrentStatus(SLACK_ACCOUNT_STATUS.EMAIL_VERIFIED);
        setNetworkStatus(NetworkState.SUCCESS);
        setMode('view');
      })
      .catch(() => {
        setNetworkStatus(NetworkState.FAILED);
      });
  };

  const handleUnlinkConfirm = () => {
    setNetworkStatus(NetworkState.IN_PROGRESS);

    unlink(degreeId)
      .then(() => {
        setSavedEmail('');
        setEmailInput('');
        setCurrentStatus(SLACK_ACCOUNT_STATUS.UNLINKED);
        setNetworkStatus(NetworkState.SUCCESS);
      })
      .catch(() => {
        setNetworkStatus(NetworkState.FAILED);
      });
  };

  const openUnlinkModal = () => {
    setShowUnlinkModal(true);
    setNetworkStatus(null);
  };

  const closeUnlinkModal = () => {
    setShowUnlinkModal(false);
    setNetworkStatus(null);
  };

  const enterEditMode = () => {
    setMode('edit');
    setNetworkStatus(null);
  };

  const cancelEdit = () => {
    setMode('view');
    setEmailInput(savedEmail);
    setNetworkStatus(null);
  };

  const renderViewMode = () => (
    <Grid container alignItems="center" justifyContent="space-between" spacing={12} ref={rowRef} tabIndex={-1}>
      <Grid item sm={12} md={9}>
        <Typography2 variant="subtitleMedium" component="h3">
          {degreeName}
        </Typography2>
        <Grid container wrap="wrap" alignItems="center" spacing={8}>
          <Grid item>
            <Typography2 variant="bodySecondary" component="p" color="supportText">
              {partnerName}
            </Typography2>
          </Grid>
          <Grid item>
            {isLinked ? (
              <Tag variant="status" color="success" priority="secondary">
                {_t('Linked')}
              </Tag>
            ) : (
              <Tag variant="status" color="error" priority="secondary">
                {_t('Not linked')}
              </Tag>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid item container sm={12} md={3} alignItems="center" spacing={8}>
        <Grid item>
          <Button
            component="a"
            href={slackTeamLink}
            variant="secondary"
            size="small"
            icon={<ExternalLinkIcon size="small" />}
            iconPosition="after"
            disabled={!isLinked}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="launch-slack-button"
          >
            {_t('Slack')}
          </Button>
        </Grid>
        <Grid item>
          <ActionOverflow size="small" aria-label={_t('More actions')}>
            <ActionOverflow.Item component="button" onClick={enterEditMode} icon={<EditIcon />}>
              {_t('Edit')}
            </ActionOverflow.Item>
            {isLinked && (
              <ActionOverflow.Item
                component="button"
                onClick={openUnlinkModal}
                data-testid="slack-account-email-unlink"
                icon={<UnlinkIcon />}
              >
                {_t('Unlink')}
              </ActionOverflow.Item>
            )}
          </ActionOverflow>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderFormMode = () => {
    const showCancel = mode === 'edit';
    const isLinking = networkStatus === NetworkState.IN_PROGRESS;
    const hasError = networkStatus === NetworkState.FAILED;

    return (
      <div>
        <Typography2 variant="subtitleMedium" component="h3">
          {degreeName}
        </Typography2>
        <Typography2 variant="bodySecondary" component="p" color="supportText">
          {partnerName}
        </Typography2>
        {!isLinked && networkStatus !== NetworkState.SUCCESS && (
          <InlineNotification severity="information" css={styles.infoNotification}>
            <FormattedMessage
              message={_t('Create a {createLink}, then enter your Slack email address below.')}
              createLink={
                <Link href={slackSignupLink} variant="standard" target="_blank" rel="noopener noreferrer">
                  {_t('free account')}
                </Link>
              }
            />
          </InlineNotification>
        )}
        <Grid container direction="column" spacing={12} css={styles.form} data-testid="accountlink">
          <Grid item>
            <TextField
              inputRef={emailInputRef}
              id={hasError ? 'SlackAccountEmailError' : 'SlackAccountEmail'}
              aria-label={_t('Slack account email')}
              placeholder={_t('Enter Slack email')}
              type="email"
              value={emailInput}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmailInput(event.target.value)}
              fullWidth
              className="pii-hide"
              data-testid="slack-account-email"
            />
          </Grid>
          <Grid item container alignItems="center" spacing={8}>
            <Grid item>
              <Button
                variant="primary"
                size="small"
                loading={isLinking}
                onClick={handleLink}
                data-testid="slack-account-email-verify"
              >
                {_t('Link')}
              </Button>
            </Grid>
            {showCancel && (
              <Grid item>
                <Button variant="ghost" size="small" onClick={cancelEdit} data-testid="slack-account-cancel">
                  {_t('Cancel')}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </div>
    );
  };

  return (
    <div id={id} data-testid="slack-row">
      {showFormView ? renderFormMode() : renderViewMode()}

      <div css={styles.liveRegion} role="status" aria-live="polite" aria-atomic="true">
        {networkStatus === NetworkState.SUCCESS && !showUnlinkModal && (
          <InlineNotification severity="success">{_t('Your Slack account has been linked.')}</InlineNotification>
        )}
      </div>
      <div css={styles.liveRegion} role="alert" aria-live="assertive" aria-atomic="true">
        {networkStatus === NetworkState.FAILED && !showUnlinkModal && (
          <InlineNotification severity="error">
            {_t(
              "We can't find a Slack account associated with this email address. Try another email or create a free account."
            )}
          </InlineNotification>
        )}
      </div>
      {showUnlinkModal && (
        <Dialog aria-describedby="dialog-content" open onClose={closeUnlinkModal}>
          {networkStatus === NetworkState.SUCCESS ? (
            <React.Fragment>
              <Dialog.HeadingGroup>{_t('Your slack account has been unlinked.')}</Dialog.HeadingGroup>
              <Dialog.Content id="dialog-content">
                <Typography2 component="p">{_t('To deactivate your account completely, go to Slack.')}</Typography2>
              </Dialog.Content>
              <Dialog.Actions>
                <Button variant="primary" onClick={closeUnlinkModal}>
                  {_t('Got it')}
                </Button>
              </Dialog.Actions>
            </React.Fragment>
          ) : (
            <UnlinkSlackConfirm
              onCancel={closeUnlinkModal}
              onConfirm={handleUnlinkConfirm}
              loading={networkStatus === NetworkState.IN_PROGRESS}
              error={networkStatus === NetworkState.FAILED}
            />
          )}
        </Dialog>
      )}
    </div>
  );
};

export default SlackRow;
